import React from 'react';
import { Box } from '@mui/material';
import { EventTimeSeries } from '../components/EventTimeSeries';
import { ExposureStats } from '../Incident/ExposureStats';
import { IncidentChart } from '../Incident/IncidentChart';
import type { IncidentSection } from '../pages/IncidentPage';
import type { ChartPoint } from '../services/weatherHistory';
import type { MapView } from '../services/types';
import { DraggableWindow } from '../components/DraggableWindow';
import { useAtomValue, useSetAtom } from 'jotai';
import { mapDrawerAtom, mapDrawerWidthAtom } from '../components/MapDrawer';
import { statsPageContextAtom } from './atoms';
import { ErrorBoundary } from '../components/ErrorBoundary';

const STATS_PANEL_WIDTH = 500;
// Home dashboard fallback while the event data (and therefore the chart's
// measured minimum height) is still loading; once EventTimeSeries reports its
// content minimum, the window follows that instead.
const HOME_STATS_PANEL_HEIGHT = 780;
const INCIDENT_STATS_PANEL_HEIGHT = 450;
// Window pixels around the content: title bar (drag handle row + border) plus
// the DraggableWindow body padding (p: 1 top and bottom).
const STATS_WINDOW_CHROME = 57;
// The map content area is `calc(100vh - 134px)` (Map.tsx containerStyles); cap
// the window so its NAV_GAP top offset and a matching bottom margin stay inside.
const MAP_AREA_TOP_OFFSET = 134;
const NAV_CONTROLS_RIGHT = 10;
const NAV_CONTROLS_WIDTH = 31; // 29px buttons + 2px Paper border
const NAV_GAP = 5;
const STATS_RIGHT_OFFSET = STATS_PANEL_WIDTH + NAV_CONTROLS_RIGHT + NAV_CONTROLS_WIDTH + NAV_GAP;

interface MapChartControlsProps {
  isHomePage: boolean;
  scatterPlotEvents: ChartPoint[] | null;
  scatterPlotLoading: boolean;
  selectedMap: MapView;
  params?: {
    incidentId?: string;
    sectionId?: string;
  };
}

export const MapChartControls: React.FC<MapChartControlsProps> = ({
  isHomePage,
  scatterPlotEvents,
  scatterPlotLoading,
  selectedMap,
  params,
}) => {
  const windowWidth = window.innerWidth;
  const drawerWidth = useAtomValue(mapDrawerWidthAtom);
  const isDrawerOpen = useAtomValue(mapDrawerAtom);
  const effectiveDrawerWidth = isDrawerOpen ? drawerWidth : 0;
  const mapContentWidth = windowWidth - effectiveDrawerWidth;

  const setStatsContext = useSetAtom(statsPageContextAtom);
  React.useEffect(() => {
    setStatsContext(isHomePage ? 'home' : 'incident');
    return () => setStatsContext(null);
  }, [isHomePage, setStatsContext]);

  // Content-measured minimum reported by EventTimeSeries; the window defaults
  // to a snug fit around it (chart at its minimum, timeline fully visible).
  const [contentMinHeight, setContentMinHeight] = React.useState<number | null>(null);
  const maxPanelHeight = window.innerHeight - MAP_AREA_TOP_OFFSET - 2 * NAV_GAP;
  const homePanelHeight =
    contentMinHeight != null
      ? Math.min(contentMinHeight + STATS_WINDOW_CHROME, maxPanelHeight)
      : HOME_STATS_PANEL_HEIGHT;

  // What the panel is showing: the dashboard's selected product, or the
  // incident and section of an incident view. A caught error clears when the
  // user moves to a different one of those.
  const panelSubject = `${selectedMap}|${params?.incidentId ?? ''}|${params?.sectionId ?? ''}`;
  const incidentSubject = `${params?.incidentId ?? ''}|${params?.sectionId ?? ''}`;

  return (
    <DraggableWindow
      title="Stats"
      initialSize={{
        width: STATS_PANEL_WIDTH,
        height: isHomePage ? homePanelHeight : INCIDENT_STATS_PANEL_HEIGHT,
        unit: 'px',
      }}
      initialPosition={{
        x: Math.max(0, mapContentWidth - STATS_RIGHT_OFFSET),
        y: NAV_GAP,
      }}
      minWidth={300}
      minHeight={200}
      darkMode={true}
      sx={{
        '@media (max-width: 1200px)': {
          transform: 'scale(0.9)',
          transformOrigin: 'top right',
        },
      }}
    >
      {/* Inside the window, so the panel's own chrome (title bar, drag, resize,
          close) survives whatever its content does -- including the time-series
          chart's click-to-detail list, which is where a null detail row
          white-screened the whole dashboard. This is also the only boundary the
          dashboard branch needs: the chart is the entire panel body there, so
          there is no sibling to keep alive. */}
      <ErrorBoundary region="The stats panel" resetKey={panelSubject}>
        <Box
          sx={{
            backgroundColor: 'transparent',
            scrollPadding: '8px',
            // The DraggableWindow body is a block container, so fill it by
            // height rather than flex. minHeight (not height) lets the incident
            // view's taller content keep scrolling within the window body.
            minHeight: '100%',
            flexDirection: 'row',
            display: 'flex',
          }}
        >
          {isHomePage ? (
            <Box
              sx={{
                pointerEvents: 'auto',
                zIndex: 10,
                ml: 1,
                width: '95%',
                // Flex items default to min-width: auto; without this the chart
                // canvas' inline width would keep the panel from shrinking narrower.
                minWidth: 0,
              }}
            >
              <EventTimeSeries
                events={scatterPlotEvents}
                loading={scatterPlotLoading}
                selectedMap={selectedMap}
                onMinHeightChange={setContentMinHeight}
              />
            </Box>
          ) : (
            <Box
              sx={{
                pointerEvents: 'auto',
                zIndex: 10,
                width: '100%',
                // Don't stretch to the flex row's height: IncidentChart's
                // height:100% box (sized for the wowcast page) would resolve
                // against the stretched height and trail hundreds of pixels of
                // empty panel after the last chart. Content-sized, the
                // percentage has no definite parent and is ignored.
                alignSelf: 'flex-start',
              }}
            >
              {/* The tiles and the charts are separate aggregations of separate
                  tables, so they fail separately: a chart that cannot render
                  must still leave the exposure counts on screen, and vice
                  versa. */}
              <Box>
                {params?.incidentId && params?.sectionId && (
                  <ErrorBoundary region="The exposure stats" resetKey={incidentSubject}>
                    <ExposureStats
                      incidentId={params.incidentId}
                      section={params.sectionId as IncidentSection}
                      viewType={'wowcast'}
                    />
                  </ErrorBoundary>
                )}
              </Box>

              <ErrorBoundary region="The incident charts" resetKey={incidentSubject}>
                <IncidentChart context="wowcast" />
              </ErrorBoundary>
            </Box>
          )}
        </Box>
      </ErrorBoundary>
    </DraggableWindow>
  );
};
